/**
 * Non-blocking bottom banner shown when a new version of RUST is ready
 * (mounted by AppProviders, contract §2/§7).
 *
 * 'Vernieuwen' posts SKIP_WAITING to the waiting worker and reloads once the
 * new worker takes control (controllerchange). Dismissible; the banner simply
 * returns on the next update event or page load.
 */
import { useEffect, useRef, useState } from 'react'
import type { PwaUpdateEvent } from './pwa-events'
import { onPwaEvent } from './pwa-events'
import './PwaUpdatePrompt.css'

export function PwaUpdatePrompt() {
  const [update, setUpdate] = useState<PwaUpdateEvent | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const reloadingRef = useRef(false)

  useEffect(() => onPwaEvent('updateAvailable', setUpdate), [])

  // Reload exactly once when the new service worker takes control.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return
    const onControllerChange = () => {
      if (reloadingRef.current) return
      reloadingRef.current = true
      window.location.reload()
    }
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
    }
  }, [])

  if (!update || dismissed) return null

  const refresh = () => {
    update.worker.postMessage({ type: 'SKIP_WAITING' })
    // Fallback: if controllerchange never fires (very old browsers), reload anyway.
    window.setTimeout(() => {
      if (!reloadingRef.current) window.location.reload()
    }, 4000)
  }

  return (
    <div className="pwa-update" role="status" aria-live="polite">
      <div className="pwa-update__card">
        <p className="pwa-update__text">Er is een nieuwe versie van RUST</p>
        <div className="pwa-update__actions">
          <button type="button" className="pwa-update__refresh" onClick={refresh}>
            Vernieuwen
          </button>
          <button
            type="button"
            className="pwa-update__dismiss"
            onClick={() => setDismissed(true)}
          >
            Nu niet
          </button>
        </div>
      </div>
    </div>
  )
}
